/**
 * Canonical, cross-cutting API error codes shared by every module and by both apps.
 *
 * The backend attaches one of these (or a per-feature code) to error envelopes; the tenant app maps
 * `code` to a localized message (`errors.api.<CODE>` for these shared codes, `<feature>.errors.<CODE>`
 * for domain codes). Keep UPPER_SNAKE_CASE: the frontend resolver only treats `/^[A-Z][A-Z0-9_]+$/`
 * tokens as machine codes.
 */
export const SHARED_API_ERROR_CODES = [
  "UNEXPECTED",
  "NETWORK",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "VALIDATION_ERROR",
  "RATE_LIMITED",
  "GONE",
  "TIMEOUT",
  "PAYLOAD_TOO_LARGE",
  "FIELD_INVALID",
] as const

export type SharedApiErrorCode = (typeof SHARED_API_ERROR_CODES)[number]

export const SHARED_API_ERROR_CODE_SET: ReadonlySet<string> = new Set(SHARED_API_ERROR_CODES)

/**
 * Default shared code for an HTTP status when a service does not attach a specific `code`. The shared
 * envelope-builder uses this so every error carries a machine code instead of leaking prose into `code`.
 */
export function statusToDefaultApiErrorCode(status: number): SharedApiErrorCode {
  switch (status) {
    case 401:
      return "UNAUTHENTICATED"
    case 403:
      return "FORBIDDEN"
    case 404:
      return "NOT_FOUND"
    case 408:
      return "TIMEOUT"
    case 409:
      return "CONFLICT"
    case 410:
      return "GONE"
    case 413:
      return "PAYLOAD_TOO_LARGE"
    case 429:
      return "RATE_LIMITED"
    case 400:
    case 422:
      return "VALIDATION_ERROR"
    default:
      if (status >= 500) return "UNEXPECTED"
      if (status <= 0) return "NETWORK"
      return "VALIDATION_ERROR"
  }
}
